import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Create,
    SimpleForm,
    TextInput,
    DateTimeInput,
    useCreate,
    useDataProvider,
    useGetIdentity,
    useNotify,
    useCanAccess,
} from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import type { CrmDataProvider } from '@/providers/types';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CardAssigneeInput } from './CardAssigneeInput';
import { CardJobs } from './CardJobs';
import { CardProjectFields } from './CardProjectFields';
import { MAX_FILE_SIZE, MAX_FILES, CARD_ACCEPT } from './cardAttachmentRules';
import { FileDropzone } from '@/components/misc/FileDropzone';
import { parseError } from './cardApi';
import type { Card, CardProjectOption } from '@/types';

export const CardCreate = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [create] = useCreate();
    const [files, setFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [project, setProject] = useState<CardProjectOption | null>(null);
    const [fieldStepId, setFieldStepId] = useState<number | null>(null);
    // Set once the user picks a project (or clears it), so a late "last project" response never overwrites it.
    const projectTouched = useRef(false);

    const { identity } = useGetIdentity();
    const isAdmin = Boolean((identity as { administrator?: boolean } | undefined)?.administrator);

    const { data: lastProject } = useQuery({
        queryKey: ['cardLastProject'],
        queryFn: () => dataProvider.getMyLastCardProject(),
    });

    useEffect(() => {
        if (lastProject && !projectTouched.current) setProject(lastProject);
    }, [lastProject]);

    const handleProjectChange = (next: CardProjectOption | null, stepId: number | null) => {
        projectTouched.current = true;
        setProject(next);
        setFieldStepId(stepId);
    };

    const { canAccess: canUpdate } = useCanAccess({
        resource: 'cards',
        action: 'edit',
    });

    const handleSubmit = async (data: any) => {
        try {
            const newCard = (await create(
                'cards',
                { data: { ...data, project_id: project?.id ?? null, field_step_id: fieldStepId } },
                { returnPromise: true }
            )) as Card;

            // Upload files if any
            if (files.length > 0) {
                setUploading(true);
                try {
                    await dataProvider.uploadCardFiles(newCard.id, files);
                    notify('Card created successfully', { type: 'success' });
                } catch (err) {
                    const message = err instanceof Error ? err.message : 'Upload failed';
                    notify(`Card created, but some files failed to upload: ${message}`, {
                        type: 'warning',
                    });
                }
            } else {
                notify('Card created successfully', { type: 'success' });
            }

            navigate(`/cards/${newCard.id}/show`, { replace: true });
        } catch (err) {
            notify(parseError((err as { body?: unknown })?.body, 'Failed to create card'), {
                type: 'error',
            });
            throw err;
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => navigate('/cards', { replace: true });

    return (
        <>
            <CardJobs />
            <Dialog
                open
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                aria-labelledby="card-create-title"
            >
                <DialogTitle id="card-create-title" sx={{ pr: 6 }}>
                    New Job
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Create resource="cards" component="div">
                        <SimpleForm onSubmit={handleSubmit}>
                <TextInput
                    source="title"
                    label="Title"
                    fullWidth
                    autoFocus
                    validate={[
                        (value: any) =>
                            value ? undefined : 'Title is required',
                        (value: any) =>
                            value && value.length <= 255
                                ? undefined
                                : 'Title must be 255 characters or less',
                    ]}
                />

                <DateTimeInput source="limit_date" label="Date" />

                <TextInput
                    source="description"
                    label="Description"
                    fullWidth
                    multiline
                    rows={4}
                />

                <CardProjectFields
                    project={project}
                    fieldStepId={fieldStepId}
                    onChange={handleProjectChange}
                />

                {/* Superusers pick an assignee (defaulting to themselves); everyone else
                    is assigned to themselves on the backend, so the input is hidden. */}
                {isAdmin && (
                    <CardAssigneeInput defaultValue={identity?.id as number | undefined} />
                )}

                {canUpdate && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Attachments (optional)
                        </Typography>
                        <FileDropzone
                            value={files}
                            onChange={setFiles}
                            maxFiles={MAX_FILES}
                            maxSize={MAX_FILE_SIZE}
                            accept={CARD_ACCEPT}
                            disabled={uploading}
                            label="Drop files here, or click to choose"
                            showHint
                        />
                    </Box>
                )}
                        </SimpleForm>
                    </Create>
                </DialogContent>
            </Dialog>
        </>
    );
};

CardCreate.displayName = 'CardCreate';
